import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { type IWarehouseProperty } from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Alert } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Link from '../link';
import Table from '../table';

interface DataType extends IWarehouseProperty {
  key: string;
}

function WarehousePropertyTable(): React.ReactElement {
  const downloaderWarehouseProperties = useSelector(
    (s: RootState) => s.Downloader.warehouse.properties,
  );
  const downloaderUpdatedAt = useSelector(
    (s: RootState) => s.Downloader.updatedAt,
  );
  const downloaderInProcess = useSelector(
    (s: RootState) => s.Downloader.inProcess,
  );

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [columns, setColumns] = useState<ColumnsType<object>>([]);

  useEffect((): void => {
    const dataSourceUE: DataType[] = [];
    const propertiesName: Set<string> = new Set<string>([]);
    const unitsName: Set<string> = new Set<string>([]);

    downloaderWarehouseProperties?.forEach(
      (property: IWarehouseProperty): void => {
        propertiesName.add(property.name);
        unitsName.add(property.unit || '');

        dataSourceUE.push({
          key: property.rawName,
          ...property,
        });
      },
    );

    const columnsUE: ColumnsType<object> = [
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.name, b.name),
        filterSearch: true,
        filters: [...propertiesName]
          .map((name: string) => ({
            text: name,
            value: name,
          }))
          .sort((a, b) => stringCompare(a.text, b.text)),
        onFilter: (value: string, record: DataType): boolean =>
          record.name.toLowerCase().includes(value.toLocaleLowerCase()),
        render: (_text: string, record: DataType): React.ReactElement => {
          const rawNameEncoded: string = encodeURIComponent(record.rawName);

          return (
            <Link
              to={`/downloader/warehouse-property/${rawNameEncoded}`}
              openInNewTab
              label={record.name}
            />
          );
        },
        width: '40%',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.unit, b.unit),
        filterSearch: true,
        filters: [...unitsName]
          .map((unit: string) => ({
            text: unit,
            value: unit,
          }))
          .sort((a, b) => stringCompare(a.text, b.text)),
        onFilter: (value: string, record: DataType): boolean =>
          record.unit?.toLowerCase().includes(value.toLowerCase()) || false,
        width: '40%',
      },
    ];

    setDataSource(dataSourceUE);
    setColumns(columnsUE);
  }, [downloaderWarehouseProperties, downloaderUpdatedAt]);

  if (downloaderWarehouseProperties === null) {
    return (
      <Alert
        title="Error"
        description="Warehouse properties not found"
        type="error"
        showIcon
      />
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      isLoading={downloaderInProcess}
      emptyText="No warehouse properties found"
    />
  );
}

export default WarehousePropertyTable;
